import { MoonIcon, SunIcon } from "@heroicons/react/16/solid";
import { Bars3Icon } from "@heroicons/react/20/solid";
import React, { FC, useEffect, useState } from "react";
import { useCallback } from "react";

import { Theme } from "@mui/material";
import {
  Box,
  IconButton,
  Link,
  Stack,
  SvgIcon,
  useMediaQuery,
} from "@mui/material";

import { Logo } from "../../components/Logo";
import { SmallLogo } from "../../components/SmallLogo";
import { AlertTriangleIcon } from "../../components/icons";
import useNavigateWithSearch from "../../hooks/use-navigate-with-search";
import { useSettings } from "../../hooks/use-settings";
import { useSelector } from "../../store";
import { useGetClusterQuery } from "../../store/pomeriumApi";
import { purple } from "../../theme/colors";
import OrgClusterDialog from "./OrgClusterDialog";
import { AccountPopover } from "./account-popover";
import ChangesetPopover from "./changeset-popover";
import { ClusterPopover } from "./cluster-popover";
import { DocsDropdown } from "./docs-dropdown";
import { useIsHostedCluster } from "@/hooks/use-is-hosted-cluster";

const TOP_NAV_HEIGHT = 64;

interface TopNavProps {
  onNavOpen?: () => void;
  openNav?: boolean;
  hideNav?: boolean;
}

const calcShowPopDownFromPathName = (pathname: string): boolean => {
  switch (pathname) {
    case "/app/organizations":
    case "/app/clusters":
    case "/onboarding":
      return false;
    default:
      return true;
  }
};

export const TopNav: FC<TopNavProps> = (props) => {
  const { onNavOpen, hideNav } = props;
  const settings = useSettings();
  const navigate = useNavigateWithSearch();
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const { data: cluster } = useGetClusterQuery(
    { organizationId, clusterId },
    { skip: !organizationId || !clusterId },
  );
  const [showPopDown, setShowPopDown] = useState(
    calcShowPopDownFromPathName(location.pathname),
  );

  const isHostedCluster = useIsHostedCluster();

  useEffect(() => {
    setShowPopDown(calcShowPopDownFromPathName(location.pathname));
  }, [location.pathname]);

  const handleThemeSwitch = useCallback((): void => {
    settings.handleUpdate({
      paletteMode: settings.paletteMode === "light" ? "dark" : "light",
    });
  }, [settings]);

  return (
    <Box
      component="header"
      sx={{
        backgroundColor: purple.main,
        color: "common.white",
        position: "fixed",
        width: "100vw",
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <OrgClusterDialog />
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          minHeight: TOP_NAV_HEIGHT,
          pl: smDown ? 0.5 : mdDown ? 1.5 : 3,
          pr: smDown ? 0.5 : mdDown ? 1 : 0.5,
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          spacing={smDown ? 0.5 : mdDown ? 1 : 3}
        >
          {!mdDown && (
            <Box
              component={Link}
              href={"/"}
              sx={{
                display: "inline-flex",
                height: 24,
                minWidth: hideNav ? 0 : 246,
              }}
            >
              <Logo height={24} />
            </Box>
          )}
          {mdDown && !hideNav && (
            <IconButton color="inherit" onClick={onNavOpen}>
              <SvgIcon color="action" fontSize="small">
                <Bars3Icon color="white" />
              </SvgIcon>
            </IconButton>
          )}
          {mdDown && (
            <Box
              component={Link}
              href={"/"}
              sx={{
                display: "inline-flex",
                height: 24,
                minWidth: hideNav || smDown ? 0 : 246,
              }}
            >
              <SmallLogo height={24} />
            </Box>
          )}
          {showPopDown && !mdDown && <ClusterPopover />}
        </Stack>
        <Stack alignItems="center" direction="row">
          <DocsDropdown />
          <IconButton color="inherit" onClick={handleThemeSwitch}>
            <SvgIcon color="action" fontSize="small">
              {settings.paletteMode === "dark" ? <SunIcon /> : <MoonIcon />}
            </SvgIcon>
          </IconButton>
          <ChangesetPopover />
          {!isHostedCluster && cluster?.hasFailingHealthChecks && (
            <IconButton
              color="warning"
              onClick={() => navigate("/app/reports/status")}
            >
              <SvgIcon color="warning" fontSize="small">
                <AlertTriangleIcon color="inherit" />
              </SvgIcon>
            </IconButton>
          )}
          <AccountPopover />
        </Stack>
      </Stack>
    </Box>
  );
};
